import { useMemo, useRef, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { exportSequenceAsZip, type ExportProgress } from './exportSequence';
import { useGalleryStore } from './galleryStore';
import { defaultTheme } from './defaultTheme';
import { SequenceList } from './SequenceList';
import { parseRawTheme } from './useGallery';
import {
  type GalleryScreenCategory,
  galleryScreenGroups,
  getScreenGroupForRoute,
  isGalleryScreenActive,
} from './screens';
import { ICON_CATALOG_ROUTE } from '../icons/catalog';
import { unlockAmountMismatchAudio } from '../utils/playAmountMismatchAlert';

type Feedback = { type: 'error' | 'success'; message: string } | null;

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'es', label: 'ES' },
  { code: 'fr', label: 'FR' },
];

const buildExpandedGroups = (
  pathname: string,
): Set<GalleryScreenCategory> => {
  const activeGroup = getScreenGroupForRoute(pathname);
  return activeGroup ? new Set([activeGroup]) : new Set();
};

export const ControlDock = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState('');
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [exportProgress, setExportProgress] = useState<ExportProgress | null>(
    null,
  );
  const [expandedGroups, setExpandedGroups] = useState<
    Set<GalleryScreenCategory>
  >(() => buildExpandedGroups(window.location.pathname));
  const fileInputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  const rawTheme = useGalleryStore((s) => s.rawTheme);
  const setRawTheme = useGalleryStore((s) => s.setRawTheme);
  const resetTheme = useGalleryStore((s) => s.resetTheme);
  const pinned = useGalleryStore((s) => s.pinned);
  const setPinned = useGalleryStore((s) => s.setPinned);
  const sequence = useGalleryStore((s) => s.sequence);
  const addToSequence = useGalleryStore((s) => s.addToSequence);
  const clearSequence = useGalleryStore((s) => s.clearSequence);
  const exporting = useGalleryStore((s) => s.exporting);
  const savedListsUi = useGalleryStore((s) => s.savedListsUi);
  const setSavedListsUi = useGalleryStore((s) => s.setSavedListsUi);

  const normalizedQuery = query.trim().toLowerCase();
  const filteredGroups = useMemo(() => {
    if (!normalizedQuery) return galleryScreenGroups;
    return galleryScreenGroups
      .map((group) => ({
        ...group,
        screens: group.screens.filter(
          (s) =>
            s.label.toLowerCase().includes(normalizedQuery) ||
            s.route.toLowerCase().includes(normalizedQuery),
        ),
      }))
      .filter((group) => group.screens.length > 0);
  }, [normalizedQuery]);

  const toggleGroup = (groupId: GalleryScreenCategory) => {
    setExpandedGroups((current) => {
      const next = new Set(current);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Seed the editor with the active theme each time the dock is opened.
  const openDock = () => {
    setDraft(JSON.stringify(rawTheme, null, 2));
    setFeedback(null);
    setQuery('');
    setExpandedGroups(buildExpandedGroups(location.pathname));
    setOpen(true);
  };

  const handleReset = () => {
    resetTheme();
    setDraft(JSON.stringify(defaultTheme, null, 2));
    setFeedback({ type: 'success', message: 'Restored default theme.' });
  };

  const goToScreen = (route: string) => {
    unlockAmountMismatchAudio();
    navigate(route);
    if (!pinned) setOpen(false);
  };

  const handleAddToSequence = (id: string) => {
    if (sequence.includes(id)) return;
    addToSequence(id);
  };

  const handleExport = async () => {
    if (sequence.length === 0 || exporting) return;
    setOpen(false);
    setFeedback(null);
    try {
      await exportSequenceAsZip({
        navigate,
        onProgress: setExportProgress,
      });
      setFeedback({
        type: 'success',
        message: `Exported ${sequence.length} screen(s) as ZIP.`,
      });
      setOpen(true);
    } catch (err) {
      setFeedback({
        type: 'error',
        message:
          err instanceof Error ? err.message : 'Could not export screens.',
      });
      setOpen(true);
    } finally {
      setExportProgress(null);
    }
  };

  const applyTheme = () => {
    try {
      const parsed = parseRawTheme(draft);
      setRawTheme(parsed);
      setFeedback({ type: 'success', message: 'Theme applied.' });
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err instanceof Error ? err.message : 'Could not apply theme.',
      });
    }
  };

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const content = String(reader.result ?? '');
      setDraft(content);
      try {
        const parsed = parseRawTheme(content);
        setRawTheme(parsed);
        setFeedback({ type: 'success', message: `Loaded ${file.name}.` });
      } catch (err) {
        setFeedback({
          type: 'error',
          message: err instanceof Error ? err.message : 'Invalid theme file.',
        });
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const copyTheme = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      setFeedback({ type: 'success', message: 'Copied to clipboard.' });
    } catch {
      setFeedback({ type: 'error', message: 'Clipboard not available.' });
    }
  };

  const changeLanguage = (code: string) => {
    void i18n.changeLanguage(code);
  };

  return (
    <>
      <button
        type="button"
        className={`gallery-fab ${exporting ? 'is-hidden' : ''}`}
        aria-label="Open Screenbook controls"
        onClick={openDock}
        hidden={exporting}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <rect x="3" y="3" width="7" height="7" rx="1.5" strokeWidth="1.8" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" strokeWidth="1.8" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" strokeWidth="1.8" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" strokeWidth="1.8" />
        </svg>
      </button>

      {exporting && exportProgress && exportProgress.total > 0 && (
        <div className="gallery-export-banner" role="status">
          Exporting {exportProgress.current}/{exportProgress.total}
          {exportProgress.screenId ? ` — ${exportProgress.screenId}` : ''}
        </div>
      )}

      <Dialog
        open={open && !exporting}
        onClose={() => {
          if (!pinned) setOpen(false);
        }}
      >
        <DialogBackdrop
          className={`gallery-backdrop ${pinned ? 'is-pinned' : ''}`}
        />
        <div className="gallery-dialog">
          <DialogPanel className="gallery-panel">
            <header className="gallery-panel-header">
              <div>
                <p className="gallery-panel-title">RaaS Screenbook</p>
                <p className="gallery-panel-subtitle">
                  Switch screens & inject a theme
                </p>
              </div>
              <div className="gallery-header-actions">
                <button
                  type="button"
                  className={`gallery-pin ${pinned ? 'is-active' : ''}`}
                  aria-label={
                    pinned
                      ? 'Unpin controls'
                      : 'Pin controls open while navigating'
                  }
                  aria-pressed={pinned}
                  title={
                    pinned
                      ? 'Pinned — stays open while navigating'
                      : 'Pin to keep open while navigating'
                  }
                  onClick={() => setPinned(!pinned)}
                >
                  <span aria-hidden="true">📌</span>
                </button>
                <button
                  type="button"
                  className="gallery-close"
                  aria-label="Close"
                  onClick={() => setOpen(false)}
                >
                  ✕
                </button>
              </div>
            </header>

            <div className="gallery-panel-body">
              <section className="gallery-section">
                <button
                  type="button"
                  className={`gallery-screen-item ${
                    location.pathname === ICON_CATALOG_ROUTE ? 'is-active' : ''
                  }`}
                  onClick={() => goToScreen(ICON_CATALOG_ROUTE)}
                >
                  <span className="gallery-screen-label">Icons</span>
                  <span className="gallery-route">{ICON_CATALOG_ROUTE}</span>
                </button>
              </section>

              <section className="gallery-section">
                <p className="gallery-section-label">Screens</p>
                <div className="gallery-search">
                  <input
                    type="text"
                    className="gallery-search-input"
                    placeholder="Search screens…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  {query && (
                    <button
                      type="button"
                      className="gallery-search-clear"
                      aria-label="Clear search"
                      onClick={() => setQuery('')}
                    >
                      ✕
                    </button>
                  )}
                </div>
                <div className="gallery-screen-groups">
                  {filteredGroups.map((group) => {
                    const isExpanded = normalizedQuery
                      ? true
                      : expandedGroups.has(group.id);
                    const hasActiveScreen = group.screens.some((screen) =>
                      isGalleryScreenActive(
                        screen.route,
                        location.pathname,
                        location.search,
                      ),
                    );

                    return (
                      <div
                        key={group.id}
                        className={`gallery-screen-group ${
                          isExpanded ? 'is-expanded' : ''
                        } ${hasActiveScreen ? 'has-active' : ''}`}
                      >
                        <button
                          type="button"
                          className="gallery-screen-group-header"
                          aria-expanded={isExpanded}
                          onClick={() => toggleGroup(group.id)}
                        >
                          <span className="gallery-screen-group-title">
                            <span
                              className="gallery-screen-group-chevron"
                              aria-hidden="true"
                            >
                              ›
                            </span>
                            {group.label}
                          </span>
                          <span className="gallery-screen-group-count">
                            {group.screens.length}
                          </span>
                        </button>
                        {isExpanded && (
                          <div className="gallery-screen-list">
                            {group.screens.map((screen) => {
                              const inSequence = sequence.includes(screen.id);
                              return (
                                <div
                                  key={screen.id}
                                  className={`gallery-screen-row ${
                                    isGalleryScreenActive(
                                      screen.route,
                                      location.pathname,
                                      location.search,
                                    )
                                      ? 'is-active'
                                      : ''
                                  }`}
                                >
                                  <button
                                    type="button"
                                    className="gallery-screen-item"
                                    title={screen.hint ?? screen.label}
                                    onClick={() => goToScreen(screen.route)}
                                  >
                                    <span className="gallery-screen-label">
                                      {screen.label}
                                    </span>
                                    <span className="gallery-route">
                                      {screen.route}
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    className={`gallery-screen-add ${
                                      inSequence ? 'is-added' : ''
                                    }`}
                                    aria-label={
                                      inSequence
                                        ? `${screen.label} already in sequence`
                                        : `Add ${screen.label} to sequence`
                                    }
                                    disabled={inSequence}
                                    onClick={() =>
                                      handleAddToSequence(screen.id)
                                    }
                                  >
                                    {inSequence ? '✓' : '+'}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {filteredGroups.length === 0 && (
                    <p className="gallery-empty">No screens found</p>
                  )}
                </div>
              </section>

              <section className="gallery-section">
                <div className="gallery-section-heading">
                  <p className="gallery-section-label">Sequence</p>
                  {sequence.length > 0 && (
                    <button
                      type="button"
                      className="gallery-link-btn"
                      onClick={clearSequence}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <SequenceList />
                <div className="gallery-sequence-actions">
                  <button
                    type="button"
                    className="gallery-btn gallery-btn-primary"
                    disabled={sequence.length === 0 || exporting}
                    onClick={() => void handleExport()}
                  >
                    Export PNGs
                  </button>
                </div>
              </section>

              <section className="gallery-section">
                <p className="gallery-section-label">Language</p>
                <div className="gallery-lang-row">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      className={`gallery-lang ${
                        i18n.resolvedLanguage === lang.code ? 'is-active' : ''
                      }`}
                      onClick={() => changeLanguage(lang.code)}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </section>

              <section className="gallery-section">
                <p className="gallery-section-label">
                  Saved lists (habits / recent)
                </p>
                <div className="gallery-lang-row">
                  {(
                    [
                      ["populated", "Chips"],
                      ["loading", "Shimmer"],
                      ["empty", "Form only"],
                    ] as const
                  ).map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      className={`gallery-lang ${
                        savedListsUi === id ? "is-active" : ""
                      }`}
                      onClick={() => setSavedListsUi(id)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </section>

              <section className="gallery-section">
                <p className="gallery-section-label">Theme (theme.json)</p>
                <a
                  className="gallery-storybook-link"
                  href="https://lola-framweork-ui.vercel.app/?path=/story/demo--font-demo"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      d="M14 4h6v6M20 4l-9 9M18 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>
                    Generate a theme in the UI Kit Storybook, then paste the
                    exported theme.json below.
                  </span>
                </a>
                <div className="gallery-theme-toolbar">
                  <button
                    type="button"
                    className="gallery-btn gallery-btn-primary"
                    onClick={applyTheme}
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    className="gallery-btn gallery-btn-secondary"
                    onClick={onPickFile}
                  >
                    Load file
                  </button>
                  <button
                    type="button"
                    className="gallery-btn gallery-btn-secondary"
                    onClick={copyTheme}
                  >
                    Copy
                  </button>
                  <button
                    type="button"
                    className="gallery-btn gallery-btn-secondary"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                </div>
                <textarea
                  className={`gallery-textarea ${
                    feedback?.type === 'error' ? 'has-error' : ''
                  }`}
                  spellCheck={false}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Paste a theme.json here…"
                />
                {feedback && (
                  <p
                    className={
                      feedback.type === 'error'
                        ? 'gallery-error'
                        : 'gallery-success'
                    }
                  >
                    {feedback.message}
                  </p>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/json,.json"
                  className="gallery-hidden-file"
                  onChange={onFileChange}
                />
              </section>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
};
